import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, ChevronRight, FileText, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getPatients, searchPatients } from "@/lib/api";
import { format } from "date-fns";

export default function Dashboard() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = search.trim()
        ? await searchPatients(search.trim())
        : await getPatients();
      setPatients(res.data);
    } catch {
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchPatients, 300);
    return () => clearTimeout(timer);
  }, [fetchPatients]);

  const filtered = statusFilter === "all"
    ? patients
    : patients.filter((p) => p.status === statusFilter);

  const counts = {
    all: patients.length,
    admitted: patients.filter((p) => p.status === "admitted").length,
    discharged: patients.filter((p) => p.status === "discharged").length,
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 tracking-tight" data-testid="dashboard-title">
            Patient Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {patients.length} patient{patients.length !== 1 ? "s" : ""} in system
          </p>
        </div>
        <Button
          onClick={() => navigate("/patients/new")}
          className="bg-slate-900 text-white hover:bg-slate-800 shadow-sm"
          data-testid="add-patient-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Add Patient
        </Button>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3" data-testid="search-filter-bar">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name or MIR number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2">
          {["all", "admitted", "discharged"].map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter(s)}
              className={statusFilter === s ? "bg-slate-900 text-white" : ""}
              data-testid={`filter-${s}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
              <span className="ml-1.5 text-xs opacity-70">{counts[s]}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Patient List */}
      {loading ? (
        <div className="grid gap-3" data-testid="loading-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-slate-100 rounded-xl p-5 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-slate-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-1/3" />
                  <div className="h-3 bg-slate-100 rounded w-1/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-100 rounded-xl p-12 text-center" data-testid="empty-state">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-slate-700 text-lg">No patients found</h3>
          <p className="text-sm text-slate-500 mt-1">
            {search ? "Try a different search term" : "Add your first patient to get started"}
          </p>
          {!search && (
            <Button
              onClick={() => navigate("/patients/new")}
              className="mt-4 bg-slate-900 text-white hover:bg-slate-800"
              data-testid="empty-add-patient-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Patient
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-3" data-testid="patient-list">
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => navigate(`/patients/${p.id}`)}
              className="bg-white border border-slate-100 rounded-xl p-5 text-left hover:shadow-md hover:border-slate-200 transition-all duration-200 group w-full"
              data-testid={`patient-card-${p.id}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 font-heading font-semibold text-sm shrink-0">
                  {p.first_name[0]}{p.last_name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-semibold text-slate-900">
                      {p.first_name} {p.last_name}
                    </span>
                    <span className="text-xs font-mono text-slate-400">MIR-{p.mir_number}</span>
                    <Badge
                      className={
                        p.status === "admitted"
                          ? "border-brand-200 bg-brand-50 text-brand-700 rounded-full text-xs"
                          : "border-emerald-200 bg-emerald-50 text-emerald-700 rounded-full text-xs"
                      }
                      variant="outline"
                    >
                      {p.status}
                    </Badge>
                    {p.has_summary && (
                      <Badge className="border-amber-200 bg-amber-50 text-amber-700 rounded-full text-xs" variant="outline">
                        <FileText className="w-3 h-3 mr-1" />
                        Summary
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                    {p.ward && <span>Ward: {p.ward}</span>}
                    {p.attending_physician && <span>Dr. {p.attending_physician}</span>}
                    <span>Admitted: {format(new Date(p.admission_date), "MMM d, yyyy")}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
